import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Zap, Shield, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo, products, services } from '../mockData';
import { Gallery } from '../components/Gallery';

export const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section with Background Image */}
      <section className="relative pt-32 pb-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 -z-10">
          <img
            src={companyInfo.refineryImage}
            alt="Industrial Facility"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.4) contrast(1.1)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#004494]/95 via-[#004494]/85 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="space-y-8">
              <div className="inline-block animate-fade-in">
                <span className="text-sm font-semibold text-[#FFD617] bg-[#FFD617]/20 px-4 py-2 rounded-full backdrop-blur-sm border border-[#FFD617]/30">
                  Industrial Excellence Since Inception
                </span>
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight font-heading drop-shadow-2xl">
                {companyInfo.tagline.split('.').slice(0, 2).join('.')}
              </h1>
              <p className="text-lg text-gray-100 leading-relaxed max-w-xl drop-shadow-lg">
                {companyInfo.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button
                    size="lg"
                    className="bg-[#FFD617] hover:bg-[#FFD617]/90 text-[#004494] px-8 py-6 text-lg rounded-lg shadow-2xl hover:shadow-[#FFD617]/50 group font-semibold"
                  >
                    Request a Quote
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/products">
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-[#004494] px-8 py-6 text-lg rounded-lg backdrop-blur-sm"
                  >
                    Explore Products
                  </Button>
                </Link>
              </div>
              {/* Trust Badges */}
              <div className="flex flex-wrap gap-6 pt-4">
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-[#FFD617]" />
                  <span className="text-sm font-medium text-white">IEC 61439 Compliant</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Shield className="w-6 h-6 text-[#FFD617]" />
                  <span className="text-sm font-medium text-white">Quality Assured</span>
                </div>
                <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <Award className="w-6 h-6 text-[#FFD617]" />
                  <span className="text-sm font-medium text-white">Industry Standard</span>
                </div>
              </div>
            </div>

            {/* Right Stats Card */}
            <div className="relative hidden lg:block">
              <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20 shadow-2xl">
                <div className="space-y-8">
                  <div className="flex items-center space-x-4 p-4 bg-[#FFD617]/20 rounded-xl">
                    <div className="bg-[#FFD617] rounded-full p-4">
                      <Zap className="w-8 h-8 text-[#004494]" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">100%</p>
                      <p className="text-sm text-gray-200">Quality Control</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl">
                    <div className="bg-white rounded-full p-4">
                      <Shield className="w-8 h-8 text-[#004494]" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">50+</p>
                      <p className="text-sm text-gray-200">Projects Delivered</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4 p-4 bg-white/10 rounded-xl">
                    <div className="bg-white rounded-full p-4">
                      <Award className="w-8 h-8 text-[#004494]" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-white">24/7</p>
                      <p className="text-sm text-gray-200">Expert Support</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 font-heading">
              About {companyInfo.name}
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {companyInfo.description}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 hover:border-[#004494] transition-all duration-300 card-hover">
              <CardContent className="pt-6">
                <div className="bg-[#FFD617]/20 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-8 h-8 text-[#004494]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 font-heading">Quality Assurance</h3>
                <p className="text-gray-600">
                  Every panel undergoes rigorous testing to meet international standards and ensure long-term reliability.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#004494] transition-all duration-300 card-hover">
              <CardContent className="pt-6">
                <div className="bg-[#FFD617]/20 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                  <Zap className="w-8 h-8 text-[#004494]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 font-heading">Innovative Solutions</h3>
                <p className="text-gray-600">
                  Cutting-edge technology and custom engineering to meet your unique industrial requirements.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#004494] transition-all duration-300 card-hover">
              <CardContent className="pt-6">
                <div className="bg-[#FFD617]/20 w-16 h-16 rounded-xl flex items-center justify-center mb-4">
                  <Award className="w-8 h-8 text-[#004494]" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3 font-heading">Expert Support</h3>
                <p className="text-gray-600">
                  Comprehensive post-installation support and maintenance to ensure optimal performance.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Products Overview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 font-heading">Our Core Products</h2>
            <p className="text-lg text-gray-600">
              Comprehensive range of electrical control panels engineered for diverse industrial applications
            </p>
          </div>

          <div className="grid md:grid-cols-3 lg:grid-cols-3 gap-8">
            {products.map((product) => (
              <Card
                key={product.id}
                className="overflow-hidden border-2 hover:border-[#004494] transition-all duration-300 card-hover cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#004494]/80 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="text-lg font-bold text-white font-heading">{product.name}</h3>
                  </div>
                </div>
                <CardContent className="pt-6">
                  <p className="text-sm text-gray-600 mb-4">{product.shortDesc}</p>
                  <Link to="/products">
                    <Button
                      variant="outline"
                      className="w-full border-[#004494] text-[#004494] hover:bg-[#004494] hover:text-white"
                    >
                      Learn More
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <Gallery />

      {/* Services Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4 font-heading">Our Services</h2>
            <p className="text-lg text-gray-600">
              End-to-end solutions from design to post-delivery support
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className="p-6 border-2 border-gray-200 rounded-xl hover:border-[#004494] transition-all duration-300 card-hover"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-[#FFD617] rounded-lg p-3 flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-[#004494]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 font-heading">
                      {service.title}
                    </h3>
                    <p className="text-sm text-gray-600">{service.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section with Background Image */}
      <section className="relative py-32 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 -z-10">
          <img
            src={companyInfo.ctaImage}
            alt="Industrial Plant"
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.35) contrast(1.2) saturate(1.1)' }}
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#004494]/90 via-[#004494]/80 to-[#003370]/90" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_rgba(0,0,0,0.3)_100%)]" />
        </div>

        <div className="max-w-4xl mx-auto text-center relative">
          <div className="bg-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/10 shadow-2xl">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 font-heading text-white drop-shadow-2xl">
              Ready to Power Your Operations?
            </h2>
            <p className="text-xl text-gray-100 mb-8 leading-relaxed drop-shadow-lg">
              Get a customized quote for your electrical control panel needs. Our team is ready to provide expert consultation and tailored solutions.
            </p>
            <Link to="/contact">
              <Button
                size="lg"
                className="bg-[#FFD617] hover:bg-[#FFD617]/90 text-[#004494] px-12 py-6 text-lg rounded-lg shadow-2xl hover:shadow-[#FFD617]/50 font-semibold transform hover:scale-105 transition-all"
              >
                Request Your Free Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            
            {/* Additional Info */}
            <div className="mt-8 flex flex-wrap justify-center gap-8 text-white">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-[#FFD617]" />
                <span className="text-sm">Free Consultation</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-[#FFD617]" />
                <span className="text-sm">24-Hour Response</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-[#FFD617]" />
                <span className="text-sm">Expert Engineering Team</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};